import React, { useState, useContext, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { UserContext } from './hooks/UserContext';
import Header from './components/Header';
import Loading from './components/Loading';
import LinksReviewPane from './components/links/LinksReviewPane';
import LinksEditModal from './components/links/LinksEditModal';
import LinksGeneratePane from './components/links/LinksGeneratePane';
import LinksGenerations from './components/links/LinksGenerations';
import useLinksPuzzles from './components/links/useLinksPuzzles';
import {
    bufferWarning, levelLabel, nextFreeDate, nextOpenDate, numberSuffix, themes, warningsOf,
} from './components/links/linksPuzzles';
import './components/links/links.css';

// The Links page (CONTEXT.md "Links puzzle"): every generated or scheduled
// puzzle on the left with running or failed generations above it, and on
// the right either the selected board (judge warnings, approve on a date)
// or the generate form. Drafts come from the form (dated) or
// `flask links-generate` (undated); only scheduled puzzles reach the app.
export const ManageLinks = () => {
    const { user } = useContext(UserContext);
    const token = user ? user.token : '';
    const links = useLinksPuzzles(token);
    const [selectedId, setSelectedId] = useState(null);
    const [editing, setEditing] = useState(null);
    const [generateFor, setGenerateFor] = useState(null);

    const selected = links.items.find(p => p.id === selectedId) || links.items[0] || null;
    const suggestedDate = useMemo(() => nextFreeDate(links.items), [links.items]);
    const banner = bufferWarning(links.buffer, links.buffer_target);

    const openGenerate = () => setGenerateFor({ date: nextOpenDate(links.items, links.generations), level: 'easy' });
    const regenerate = (puzzle) => setGenerateFor({ date: puzzle.date, level: puzzle.level });
    const select = (id) => { setGenerateFor(null); setSelectedId(id); };
    const generate = async (request) => {
        if (await links.generate(request)) setGenerateFor(null);
    };

    const statusCell = (p) => {
        const number = numberSuffix(p);
        if (p.status === 'approved') return <span className="lk-pill lk-pill-approved">{p.date}{number}</span>;
        return <span className="lk-pill lk-pill-draft">{p.date ? `Draft · ${p.date}` : 'Draft'}{number}</span>;
    };
    const levelCell = (p) => <span className={`lk-level lk-level-${p.level}`}>{levelLabel(p.level)}</span>;
    const warningsCell = (p) => {
        const count = warningsOf(p).length;
        return count ? <span className="lk-warn-count" title={warningsOf(p).join('\n')}>{count} ⚠</span> : '';
    };

    const closeEdit = async (saved) => {
        setEditing(null);
        if (saved) await links.reload();
    };

    if (links.loading) return (<><Header /><Loading /></>);

    return (
        <div>
            <Header />
            <div className="lk-page">
                {banner && <div className="lk-banner">{banner}</div>}
                {links.error && <div className="lk-error">{links.error}</div>}
                <div className="lk-layout">
                    <div className="lk-list">
                        <div className="lk-list-head">
                            <span>{links.items.length} puzzles · {links.buffer} days scheduled ahead</span>
                            <button type="button" className="lk-btn lk-btn-primary" onClick={openGenerate}>Generate…</button>
                        </div>
                        <LinksGenerations generations={links.generations} />
                        <DataTable
                            value={links.items}
                            selectionMode="single"
                            selection={selected}
                            onSelectionChange={e => e.value && select(e.value.id)}
                            dataKey="id"
                            className="p-datatable-sm"
                            emptyMessage="No puzzles yet — generate one."
                        >
                            <Column field="id" header="#" style={{ width: '4rem' }} />
                            <Column header="Scheduled" body={statusCell} style={{ width: '9rem' }} />
                            <Column header="Level" body={levelCell} style={{ width: '5.5rem' }} />
                            <Column header="Themes" body={themes} />
                            <Column header="" body={warningsCell} style={{ width: '4rem' }} />
                        </DataTable>
                    </div>
                    <div className="lk-detail">
                        {generateFor && (
                            <LinksGeneratePane
                                token={token}
                                initial={generateFor}
                                puzzles={links.items}
                                generations={links.generations}
                                busy={links.busy}
                                onGenerate={generate}
                                onCancel={() => setGenerateFor(null)}
                            />
                        )}
                        {!generateFor && (selected ? (
                            <LinksReviewPane
                                puzzle={selected}
                                puzzles={links.items}
                                suggestedDate={suggestedDate}
                                busy={links.busy}
                                onApprove={links.approve}
                                onUnschedule={links.unschedule}
                                onSetNumber={links.setNumber}
                                onRegenerate={regenerate}
                                onEdit={setEditing}
                                onDelete={links.remove}
                            />
                        ) : <p className="lk-muted">Select a puzzle.</p>)}
                    </div>
                </div>
            </div>
            <LinksEditModal puzzle={editing} token={token} onClose={closeEdit} />
        </div>
    );
};

export default ManageLinks;
